import { IconClockRecord } from "../control/ui/icons";
import { GoldChip } from "../control/ui/Gold";
import { nextUtcMidnight } from "../lib/wallAllowance";
import { wallLeftLabel } from "./wallLeftLabel";

/**
 * The daily wall allowance, on the glass — beside the goon session in the bar's session
 * slot, sized to be read from across a room like everything else there.
 *
 * WHY A GAUGE AT ALL. A screen that shrinks with no warning reads as broken; a screen
 * that shows the meter draining reads as a trade — and the trade is the feature: fewer
 * tiles last longer, which the number says out loud in wall-minutes. Two states: filling
 * ("1h 20m left at 9 tiles", the bar draining), and spent (amber, the tile count, the hour
 * it comes back, and the one door out — Premium). Absent entirely for Gold, for a wall
 * view, and while the caps are dark: no meter, no gauge.
 *
 * Clock time for THIS wall, not raw wall-minutes: at sixteen tiles "3h" of wall-minutes
 * would be a lie by a factor of sixteen. The remaining figure is divided by the tiles
 * currently playing and the label names that count — "4h 00m left at 4 tiles" is the trade
 * made visible without asking anyone to do arithmetic. Words in ./wallLeftLabel.
 */
export function WallAllowanceGauge({
  usedSeconds,
  totalSeconds,
  tilesPlaying,
  exhausted,
  keptTiles,
  onGoPremium,
}: {
  usedSeconds: number;
  totalSeconds: number;
  /** Tiles counting right now — divides the remainder into clock time for this wall. */
  tilesPlaying: number;
  exhausted: boolean;
  keptTiles: number;
  onGoPremium: () => void;
}) {
  const left = Math.max(0, totalSeconds - usedSeconds);
  const frac = totalSeconds > 0 ? Math.min(1, usedSeconds / totalSeconds) : 1;
  const perTile = tilesPlaying > 0 ? left / tilesPlaying : left;
  const resetsAt = new Date(nextUtcMidnight(Date.now()));
  const resetLabel = resetsAt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (exhausted) {
    return (
      <div
        className="flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-sm text-amber-200"
        data-allowance="spent"
      >
        <IconClockRecord width={14} height={14} className="shrink-0 text-amber-300" />
        <span className="whitespace-nowrap">
          Wall time's up — {keptTiles} tiles till {resetLabel}
        </span>
        <button
          type="button"
          onClick={onGoPremium}
          className="ml-1 inline-flex items-center gap-1 rounded-full bg-[var(--color-accent)] px-2.5 py-0.5 text-xs font-semibold text-black hover:opacity-90"
        >
          <GoldChip /> Keep the full wall
        </button>
      </div>
    );
  }
  return (
    <div
      className="flex items-center gap-2 text-sm text-zinc-300"
      data-allowance="filling"
      title={`${Math.round(left / 60).toLocaleString()} wall-minutes left today — resets ${resetLabel}`}
    >
      <IconClockRecord width={14} height={14} className="shrink-0 text-zinc-400" />
      <span className="relative h-1.5 w-16 overflow-hidden rounded-full bg-white/10" aria-hidden>
        <span
          className={`absolute inset-y-0 left-0 rounded-full ${frac > 0.85 ? "bg-amber-400" : "bg-[var(--color-accent)]"}`}
          style={{ width: `${Math.max(2, (1 - frac) * 100)}%` }}
        />
      </span>
      <span className="whitespace-nowrap tabular-nums">{wallLeftLabel(perTile, tilesPlaying)}</span>
    </div>
  );
}
